import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Loader2, TrendingUp, Wallet, UserPlus, UserMinus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const RANGES = [
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

export default function StudioRevenue() {
  const [tenant, setTenant] = useState(null);
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/tenant/me").then((r) => alive && setTenant(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/cms/analytics/revenue?range=${range}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range]);

  const themeColor = tenant?.theme_color || "#7C3AED";
  const accentColor = tenant?.accent_color || "#F59E0B";
  const currency = data?.currency || tenant?.default_currency || "INR";

  const chartData = useMemo(() => {
    if (!data?.revenue_by_day) return [];
    return data.revenue_by_day.map((d) => ({
      date: fmtDate(d.date),
      amount: d.amount,
      amount_major: Number(d.amount || 0) / 100,
    }));
  }, [data]);

  return (
    <StudioLayout tenant={tenant}>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap" data-testid="studio-revenue-header">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Studio admin · Revenue</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Revenue &amp; conversion</h1>
          <p className="text-white/50 mt-1 text-sm">MRR, transactions, and which titles are converting paywall views.</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 border border-amber-500/30 bg-amber-500/5 text-[10px] uppercase tracking-[0.3em] text-amber-200" data-testid="mock-payments-banner">
            Mock payments • amounts reflect simulated transactions
          </div>
        </div>
        <RangeSelector range={range} setRange={setRange} themeColor={themeColor} />
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20 text-white/40 text-sm uppercase tracking-widest" data-testid="studio-revenue-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="studio-revenue-kpis">
            <Kpi label="MRR estimate" value={formatMoney(data.mrr_estimate, currency)} icon={<Wallet className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-mrr" />
            <Kpi label={`Revenue · ${range}`} value={formatMoney(data.revenue_total, currency)} icon={<TrendingUp className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-revenue" />
            <Kpi label="Active subs" value={data.active_subscriptions.toLocaleString()} icon={<UserPlus className="w-3.5 h-3.5" />} accent={themeColor} testid="kpi-active-subs" />
            <Kpi label={`Cancelled · ${range}`} value={data.cancelled_in_range.toLocaleString()} icon={<UserMinus className="w-3.5 h-3.5" />} accent={accentColor} testid="kpi-cancelled" />
          </div>

          {/* Revenue chart */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="chart-revenue-by-day">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Revenue by day</div>
                <h3 className="font-display font-bold text-lg mt-1">{currency} succeeded transactions</h3>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5" style={{ backgroundColor: themeColor }} /> Revenue ({currency})</span>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                  <defs>
                    <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={themeColor} stopOpacity={0.55} />
                      <stop offset="100%" stopColor={themeColor} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#ffffff14" vertical={false} />
                  <XAxis dataKey="date" stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #ffffff20", borderRadius: 0, fontSize: 12 }}
                    labelStyle={{ color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                    formatter={(v) => [formatMoney(v, currency), "Revenue"]}
                  />
                  <Area type="monotone" dataKey="amount" stroke={themeColor} strokeWidth={2} fill="url(#rev-grad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Top converting */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="top-converting-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top converting titles</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Paywall views → purchases</h3>
              {(data.top_converting_titles || []).length === 0 ? (
                <EmptyState text="No paywall views or conversions yet in this range." />
              ) : (
                <div className="space-y-2">
                  {data.top_converting_titles.map((t, idx) => (
                    <Link
                      key={t.content_item_id}
                      to={`/studio/catalogue/${t.content_item_id}`}
                      className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/20 transition-colors"
                      data-testid={`converting-${t.slug}`}
                    >
                      <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5">
                          {t.paywall_views} paywall views · {t.conversions} purchases
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono" style={{ color: accentColor }}>{(t.conversion_rate * 100).toFixed(1)}%</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">conv rate</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Subscription movement */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="subs-movement-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Subscription movement</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Net change in this window</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-emerald-400/20 bg-emerald-400/5 p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80">New subs</div>
                  <div className="mt-2 font-display font-bold text-3xl text-emerald-200">{data.new_in_range}</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/35 mt-2">started in {range}</div>
                </div>
                <div className="border border-rose-400/20 bg-rose-400/5 p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-rose-300/80">Cancellations</div>
                  <div className="mt-2 font-display font-bold text-3xl text-rose-200">{data.cancelled_in_range}</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/35 mt-2">cancelled in {range}</div>
                </div>
              </div>
              <div className="mt-5 border border-white/10 p-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Net</div>
                  <div className="font-display font-bold text-lg mt-1">
                    {data.new_in_range - data.cancelled_in_range >= 0 ? "+" : ""}{data.new_in_range - data.cancelled_in_range}
                  </div>
                </div>
                <Link to="/studio/billing" className="text-[10px] uppercase tracking-[0.3em] text-white/60 hover:text-white">
                  Billing settings →
                </Link>
              </div>
            </div>
          </div>

          {/* Recent transactions */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="recent-transactions-card">
            <div className="flex items-center gap-2 mb-5">
              <Receipt className="w-4 h-4 text-white/50" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Recent transactions</div>
                <h3 className="font-display font-bold text-lg mt-0.5">Last 20 succeeded</h3>
              </div>
            </div>
            {(data.recent_transactions || []).length === 0 ? (
              <EmptyState text="No succeeded transactions in this range." />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-[10px] uppercase tracking-[0.25em] text-white/40">
                      <th className="text-left py-2">Date</th>
                      <th className="text-left py-2">User</th>
                      <th className="text-left py-2">Type</th>
                      <th className="text-right py-2">Amount</th>
                      <th className="text-left py-2 pl-3">Provider</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_transactions.map((t, i) => (
                      <tr key={i} className="border-b border-white/5" data-testid={`txn-row-${i}`}>
                        <td className="py-3 text-white/70">{fmtDateTime(t.date)}</td>
                        <td className="py-3 text-white/70 truncate">{t.user_email || "—"}</td>
                        <td className="py-3 uppercase tracking-[0.2em] text-[10px] text-white/55">{t.kind}</td>
                        <td className="py-3 text-right font-mono">{formatMoney(t.amount, t.currency)}</td>
                        <td className="py-3 pl-3 uppercase tracking-[0.2em] text-[10px] text-white/55">{t.provider}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </StudioLayout>
  );
}

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/45">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="mt-3 font-display font-bold text-2xl tracking-tight">{value}</div>
    </div>
  );
}

function RangeSelector({ range, setRange, themeColor }) {
  return (
    <div className="inline-flex border border-white/10 bg-[#121212]" data-testid="revenue-range-selector">
      {RANGES.map((r) => {
        const active = range === r.id;
        return (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors ${active ? "text-black" : "text-white/60 hover:text-white"}`}
            style={active ? { backgroundColor: themeColor } : {}}
            data-testid={`revenue-range-${r.id}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-white/10 p-8 text-center text-white/45 text-sm">
      {text}
    </div>
  );
}
